/**
 * Agent Runner Types
 */

/** Agent definition (local copy to avoid dependency issues) */
export interface AgentDefinition {
	name: string;
	model: string;
}

/** Agent runner configuration */
export interface AgentRunnerConfig {
	/** Agent definition */
	agent: AgentDefinition;
	/** Tool policies */
	toolPolicy?: ToolPolicy;
	/** Sandbox configuration */
	sandbox?: SandboxConfig;
	/** Bootstrap context files */
	bootstrapFiles?: string[];
	/** Memory configuration */
	memory?: MemoryConfig;
}

/** Tool policy configuration */
export interface ToolPolicy {
	/** Allowed tools (if set, only these tools are allowed) */
	allow?: string[];
	/** Denied tools (always blocked) */
	deny?: string[];
	/** Tool groups allowed */
	allowGroups?: string[];
	/** Tool groups denied */
	denyGroups?: string[];
}

/** Sandbox configuration */
export interface SandboxConfig {
	/** Enable sandboxing */
	enabled: boolean;
	/** Docker image to use */
	image?: string;
	/** Workspace mount */
	workspace?: string;
	/** Read-only mounts */
	readOnlyMounts?: Record<string, string>;
	/** Read-write mounts */
	readWriteMounts?: Record<string, string>;
	/** Environment variables */
	env?: Record<string, string>;
	/** Resource limits */
	limits?: {
		memory?: string;
		cpus?: string;
		timeout?: number;
	};
}

/** Memory configuration */
export interface MemoryConfig {
	/** Enable memory */
	enabled: boolean;
	/** Memory files to load */
	files?: string[];
	/** Auto-save threshold (tokens) */
	autoSaveThreshold?: number;
}

/** Agent context */
export interface AgentContext {
	/** Session key */
	sessionKey: string;
	/** Agent ID */
	agentId: string;
	/** Channel type */
	channel: string;
	/** Workspace directory */
	workspace: string;
	/** Conversation history */
	history: unknown[];
	/** Memory content */
	memory?: string;
}

/** Agent execution result */
export interface AgentResult {
	/** Success status */
	success: boolean;
	/** Response content */
	content?: string;
	/** Tool calls made */
	toolCalls?: unknown[];
	/** Usage statistics */
	usage?: {
		inputTokens: number;
		outputTokens: number;
		totalTokens: number;
	};
	/** Error message */
	error?: string;
}
